import { LogLevel, LogManager, TimestampFormat } from './Logger';

import type { Command } from '../editor/commands';

/**
 * Logger channels used by VideoEditorLogger
 */
export enum VideoEditorLogChannel {
  COMMANDS = 'video-editor:commands',
  STATE = 'video-editor:state',
  TIMELINE = 'video-editor:timeline',
  TRANSCRIPT = 'video-editor:transcript',
  BATCHING = 'video-editor:batching',
  PERFORMANCE = 'video-editor:performance',
  KARAOKE = 'video-editor:karaoke',
}

/**
 * VideoEditorLogger - Specialized logger for VideoEditor operations
 * Provides consistent logging for VideoEditor commands and operations
 */
export class VideoEditorLogger {
  private static commandLogger = LogManager.getLogger(
    VideoEditorLogChannel.COMMANDS,
  );
  private static stateLogger = LogManager.getLogger(
    VideoEditorLogChannel.STATE,
  );
  private static timelineLogger = LogManager.getLogger(
    VideoEditorLogChannel.TIMELINE,
  );
  private static transcriptLogger = LogManager.getLogger(
    VideoEditorLogChannel.TRANSCRIPT,
  );
  private static batchLogger = LogManager.getLogger(
    VideoEditorLogChannel.BATCHING,
  );
  private static perfLogger = LogManager.getLogger(
    VideoEditorLogChannel.PERFORMANCE,
    {
      timestampFormat: TimestampFormat.RELATIVE, // Performance logs show relative timing
    },
  );
  private static karaokeLogger = LogManager.getLogger(
    VideoEditorLogChannel.KARAOKE,
  );

  /**
   * Initialize the VideoEditorLogger with default settings
   * Optional - only needed if you want to change defaults
   */
  static initialize(
    options: {
      commandsEnabled?: boolean;
      stateEnabled?: boolean;
      timelineEnabled?: boolean;
      transcriptEnabled?: boolean;
      batchingEnabled?: boolean;
      performanceEnabled?: boolean;
      karaokeEnabled?: boolean;
      commandLevel?: LogLevel;
      performanceLevel?: LogLevel;
    } = {},
  ): void {
    // Set default levels
    if (options.commandLevel !== undefined) {
      this.commandLogger.setLevel(options.commandLevel);
    }

    // Configure which channels are enabled
    if (options.commandsEnabled !== undefined) {
      this.commandLogger.setEnabled(options.commandsEnabled);
    }
    if (options.stateEnabled !== undefined) {
      this.stateLogger.setEnabled(options.stateEnabled);
    }
    if (options.timelineEnabled !== undefined) {
      this.timelineLogger.setEnabled(options.timelineEnabled);
    }
    if (options.transcriptEnabled !== undefined) {
      this.transcriptLogger.setEnabled(options.transcriptEnabled);
    }
    if (options.batchingEnabled !== undefined) {
      this.batchLogger.setEnabled(options.batchingEnabled);
    }
    if (options.performanceEnabled !== undefined) {
      this.perfLogger.setEnabled(options.performanceEnabled);
    }
    if (options.karaokeEnabled !== undefined) {
      this.karaokeLogger.setEnabled(options.karaokeEnabled);
    }
  }

  /**
   * Log command execution
   * @param command The command being executed
   * @param phase Execution phase (start, complete, or error)
   * @param details Optional additional details
   */
  static logCommand(
    command: Command,
    phase: 'start' | 'complete' | 'error',
    details?: any,
  ): void {
    // Format and log the command details
    const commandName = command.getName();
    const commandDesc = command.getDescription();

    switch (phase) {
      case 'start':
        this.commandLogger.debug(
          `⏳ Executing: ${commandName} - ${commandDesc}`,
          { ...details, commandName, commandDesc },
        );
        break;
      case 'complete':
        this.commandLogger.info(
          `✅ Completed: ${commandName} - ${commandDesc}`,
          { ...details, commandName, commandDesc },
        );
        break;
      case 'error':
        this.commandLogger.error(`❌ Failed: ${commandName} - ${commandDesc}`, {
          ...details,
          commandName,
          commandDesc,
        });
        break;
    }
  }

  /**
   * Log command undo operation
   * @param command The command being undone
   * @param success Whether the undo operation was successful
   * @param details Optional additional details
   */
  static logCommandUndo(
    command: Command,
    success: boolean,
    details?: any,
  ): void {
    const commandName = command.getName;
    const commandDesc = command.getDescription();

    if (success) {
      this.commandLogger.info(`↩️ Undone: ${commandName} - ${commandDesc}`, {
        ...details,
        commandName,
        commandDesc,
      });
    } else {
      this.commandLogger.error(
        `⚠️ Undo failed: ${commandName} - ${commandDesc}`,
        { ...details, commandName, commandDesc },
      );
    }
  }

  /**
   * Log command redo operation
   * @param command The command being redone
   * @param success Whether the redo operation was successful
   * @param details Optional additional details
   */
  static logCommandRedo(
    command: Command,
    success: boolean,
    details?: any,
  ): void {
    const commandName = command.getName();
    const commandDesc = command.getDescription();

    if (success) {
      this.commandLogger.info(`↪️ Redone: ${commandName} - ${commandDesc}`, {
        ...details,
        commandName,
        commandDesc,
      });
    } else {
      this.commandLogger.error(
        `⚠️ Redo failed: ${commandName} - ${commandDesc}`,
        { ...details, commandName, commandDesc },
      );
    }
  }

  /**
   * Log editor state changes
   * @param source Source of the state change
   * @param details Additional state details
   */
  static logStateChange(source: string, details?: any): void {
    this.stateLogger.debug(`State changed (${source})`, details);
  }

  /**
   * Log timeline-specific changes
   * @param operation The timeline operation
   * @param details Additional details
   */
  static logTimelineOperation(operation: string, details?: any): void {
    this.timelineLogger.debug(`Timeline: ${operation}`, details);
  }

  /**
   * Log transcript-specific operations
   * @param operation The transcript operation
   * @param details Additional details
   */
  static logTranscriptOperation(operation: string, details?: any): void {
    this.transcriptLogger.debug(`Transcript: ${operation}`, details);
  }

  /**
   * Log batch operations
   * @param phase Batch phase (begin, end, abort)
   * @param depth Current batch depth
   * @param details Additional details
   */
  static logBatch(
    phase: 'begin' | 'end' | 'abort',
    depth: number,
    details?: any,
  ): void {
    switch (phase) {
      case 'begin':
        this.batchLogger.debug(`Batch begin (depth: ${depth})`, details);
        break;
      case 'end':
        this.batchLogger.debug(`Batch end (depth: ${depth})`, details);
        break;
      case 'abort':
        this.batchLogger.warn(`Batch aborted (depth: ${depth})`, details);
        break;
    }
  }

  /**
   * Log performance measurements
   * @param operation The operation being measured
   * @param durationMs Duration in milliseconds
   * @param details Additional details
   */
  static logPerformance(
    operation: string,
    durationMs: number,
    details?: any,
  ): void {
    this.perfLogger.debug(`${operation}: ${durationMs.toFixed(2)}ms`, details);
  }

  /**
   * Start a performance measurement
   * Returns a function that, when called, will log the elapsed time
   * @param operation The operation being measured
   * @returns Function to call when operation completes
   */
  static timeOperation(operation: string): () => void {
    const startTime = performance.now();
    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      this.logPerformance(operation, duration);
    };
  }

  /**
   * Log karaoke-specific operations
   * @param operation The karaoke operation
   * @param details Additional details
   */
  static logKaraoke(operation: string, details?: any): void {
    this.karaokeLogger.debug(`Karaoke: ${operation}`, details);
  }
}

// Initialize with default settings
// This can be called again later to override settings
VideoEditorLogger.initialize({
  // Enable command logging by default, others can be enabled as needed
  commandsEnabled: true,
  stateEnabled: false,
  timelineEnabled: false,
  transcriptEnabled: false,
  batchingEnabled: false,
  performanceEnabled: false,
  karaokeEnabled: true,

  // Default log levels
  commandLevel: LogLevel.INFO,
});
